import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpService} from '@/common/http/http.service';
import {HTTP_ENDPOINTS} from '@/common/http/http.endpoints';
import {ApiResponseInterface} from '@/common/http/http.interface';

/**
 * A project's sync health: anyone who can view the project reads it
 */
@Injectable({
	providedIn: 'root'
})
export class SyncHealthService {
	constructor(
		private http: HttpService,
	) {
	}

	/**
	 * The open tickets the import would copy, and how many another project holds (task 7.1). Needs update_project. A
	 * 409 (`not_linked`, `not_receiving` with `receiving`, `inactive`) and a 503 (`sifter_unavailable`) carry
	 * `data.reason`.
	 */
	importPreview(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.importPreview));
	}

	/**
	 * Queues the import of the confirmed tickets (202). A 409 `import_running` while another import of the project runs.
	 */
	importTickets(projectId: number | string, numbers: string[]): Observable<ApiResponseInterface> {
		return this.http.post(this.url(projectId, HTTP_ENDPOINTS.projectSync.import), {numbers});
	}

	/**
	 * The project's latest import, or null data
	 */
	importRun(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.import));
	}

	summary(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.summary));
	}

	/**
	 * The failing copies, a page at a time: with beforeTracking, only the ones from before tracking began, else all
	 * the others
	 */
	failures(projectId: number | string, args: {beforeTracking: boolean; page: number; perPage: number}): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.failures), {
			beforeTracking: args.beforeTracking ? 1 : 0,
			page: args.page,
			perPage: args.perPage,
		});
	}

	/**
	 * The Slack messages CrowdSync couldn't handle, which no later message settled, a page at a time
	 */
	problems(projectId: number | string, args: {page: number; perPage: number}): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.events), {
			outcome: 'problem',
			page: args.page,
			perPage: args.perPage,
		});
	}

	/**
	 * The project's copies with these ids, whatever their state: a retried copy leaves the failures list whether it
	 * was copied or closed, so the page follows it here
	 */
	copies(projectId: number | string, ids: number[]): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.copies), {'ids[]': ids});
	}

	/**
	 * The project's latest link check, or null data when it was never checked (task 4.2)
	 */
	linkCheck(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.linkCheck));
	}

	/**
	 * Checks the project's links now and stores the run: its answer is the run, as linkCheck() gives it. Needs
	 * update_project, and the API allows ten a minute (task 4.2).
	 */
	checkLinks(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.post(this.url(projectId, HTTP_ENDPOINTS.projectSync.linkCheck), {});
	}

	/**
	 * The held copies, a page at a time, with `total` (task 4.7)
	 */
	held(projectId: number | string, args: {page: number; perPage: number}): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.held), {page: args.page, perPage: args.perPage});
	}

	/**
	 * Sends every held copy into the current Productive link (task 4.7). Needs update_project. A 409 (the link is still
	 * archived, missing, refused or gone) and a 503 (Productive didn't answer) carry the reason in `data.reason`.
	 */
	sendHeld(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.post(this.url(projectId, HTTP_ENDPOINTS.projectSync.sendHeld), {});
	}

	/**
	 * The copies created in each archived Productive project after its archive date (task 4.7)
	 */
	archivedCopies(projectId: number | string): Observable<ApiResponseInterface> {
		return this.http.get(this.url(projectId, HTTP_ENDPOINTS.projectSync.archivedCopies));
	}

	/**
	 * Queues a retry of these copies, or of every copy that failed since tracking began. Needs update_project.
	 */
	retry(projectId: number | string, body: {task_ids: number[]} | {all: true}): Observable<ApiResponseInterface> {
		return this.http.post(this.url(projectId, HTTP_ENDPOINTS.projectSync.retry), body);
	}

	/**
	 * The archived-project alerts of every active project the user can edit, for the banner above every page (task
	 * 4.8). Without update_project the list is empty.
	 */
	alerts(): Observable<ApiResponseInterface> {
		return this.http.get(HTTP_ENDPOINTS.sync.alerts);
	}

	private url(projectId: number | string, path: string): string {
		return `${HTTP_ENDPOINTS.projectSync.base}/${projectId}/${path}`;
	}
}
